import { allEnumValues, MenuItemDisplayStyle, MenuSectionDesignation, MenuSectionHeaderType, MenuSectionPriceDisplay, QuickAddEligibility, UserRight, } from '@biteinc/enums';
import { SyncableMenuModelSchema } from './syncable_menu_model_schema.js';
export const menuSectionSchema = {
    displayName: 'menu section',
    type: 'object',
    fields: {
        ...SyncableMenuModelSchema.fields,
        designation: {
            type: 'stringEnum',
            enum: MenuSectionDesignation,
            ui: 'dropdown',
            writeRight: UserRight.ManageMenu,
            defaultValue: MenuSectionDesignation.Regular,
            required: true,
        },
        name: {
            type: 'shortString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        internalName: {
            type: 'shortString',
            displayName: 'Internal Name',
            collapse: true,
            tooltip: {
                text: 'A name used only to identify this section internally. Not seen by guests.',
            },
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        posName: {
            type: 'shortString',
            displayName: 'POS Name',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display name. The field “Display Name” will replace this name on the kiosk.',
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        posInternalName: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        fulfillmentName: {
            type: 'shortString',
            displayName: 'Fulfillment Name',
            tooltip: {
                text: 'This is the name that will be displayed on the KDS for fulfillment integrations.',
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        description: {
            type: 'longString',
            canHaveLocalizationOverrides: true,
            tooltip: {
                text: "Text shown between this section's title and the items.",
            },
            writeRight: UserRight.ManageMenu,
        },
        posDescription: {
            type: 'longString',
            displayName: 'POS Description',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display description. The field “Display Description” will replace this description on the kiosk.',
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        headerType: {
            displayName: 'Header Type',
            type: 'stringEnum',
            enum: MenuSectionHeaderType,
            ui: 'dropdown',
            writeRight: UserRight.ManageMenu,
            isNullable: true,
            tooltip: {
                text: 'Vitrine only.',
                isWarning: true,
            },
            conditions: [
                {
                    field: 'designation',
                    values: [
                        MenuSectionDesignation.Regular,
                        MenuSectionDesignation.OrderHistory,
                        MenuSectionDesignation.RecommendationPane,
                    ],
                },
            ],
        },
        vendorId: {
            type: 'mongoId',
            displayName: 'Vendor',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        minItemCount: {
            type: 'int',
            displayName: 'Min Item Count',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: "If set, the section will only show if it has at least N items in the guest's order history. Vitrine only.",
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.OrderHistory],
                },
            ],
            min: 0,
        },
        maxItemCount: {
            type: 'int',
            displayName: 'Max Item Count',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: "If set, the section will only show the first N items of the guest's order history. Vitrine only.",
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.OrderHistory],
                },
            ],
        },
        images: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'fit-width',
            size: {
                width: 1080,
                height: 200,
            },
            tooltip: {
                text: 'With sidebar we recommend 720x200px, without 1080x200px',
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        pageNavigationImages: {
            displayName: 'Page navigation image',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            // One manually uploaded and one synced
            maxCount: 2,
            keepAspectRatio: 'contain',
            size: {
                width: 1200,
                height: 1200,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'This image will be used on the page navigation screen.',
            },
        },
        defaultItemDisplayStyle: {
            type: 'int',
            values: allEnumValues(MenuItemDisplayStyle),
            displayName: 'Default Item Display Style',
            ui: 'dropdown',
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
            writeRight: UserRight.ManageMenu,
        },
        useFullScreenCustomizationFlow: {
            type: 'bool',
            displayName: 'Use Full Screen Customization Flow for all items',
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        priceDisplay: {
            type: 'int',
            values: allEnumValues(MenuSectionPriceDisplay),
            displayName: 'Show Prices',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        quickAddOverride: {
            displayName: 'Quick Add Override',
            type: 'object',
            isNullable: true,
            defaultValue: null,
            writeRight: UserRight.ManageMenu,
            fields: {
                isEnabled: {
                    displayName: 'Enable quick add',
                    type: 'bool',
                    defaultValue: true,
                    required: true,
                },
                eligibility: {
                    displayName: 'Quick add eligibility',
                    type: 'stringEnum',
                    enum: QuickAddEligibility,
                    ui: 'dropdown',
                    isNullable: true,
                },
            },
        },
        hideFromMenu: {
            type: 'bool',
            displayName: 'Hide from menu',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'Checking this will hide the section from the menu. Items within the section will still be orderable if the client has a barcode, or if the item is a paired recommendation.',
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        posHideFromMenu: {
            displayName: 'POS Hide from Menu',
            type: 'bool',
            isPermanent: true,
            isReadOnly: true,
        },
        activeTimings: {
            type: 'array',
            elementType: 'object',
            collapse: true,
            displayName: 'Show Only During',
            placeholder: 'Select a Timing',
            warningHtml: 'This section will only be shown on the menu during the times you selected. If nothing is selected, this section will always be shown.',
            requiresTimings: true,
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        fulfillmentStationIds: {
            type: 'array',
            elementType: 'mongoId',
            ui: 'table',
            displayName: 'Fulfillment Stations',
            placeholder: 'Select a Station',
            tooltip: {
                text: 'Only applies to Fresh KDS. The KDS stations where items in this section will be sent.',
            },
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        footnote: {
            type: 'shortString',
            tooltip: {
                text: 'Text shown at the end of this section, after the items.',
            },
            writeRight: UserRight.ManageMenu,
            canHaveLocalizationOverrides: true,
        },
        items: {
            type: 'array',
            elementType: 'object',
            maxCount: 5000,
            isHidden: true,
            isPermanent: true,
            fields: {
                _id: {
                    type: 'mongoId',
                    isHidden: true,
                    isPermanent: true,
                    required: true,
                },
                isCompoundItem: {
                    type: 'bool',
                    isHidden: true,
                    isPermanent: true,
                },
            },
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
        i9nItems: {
            type: 'array',
            elementType: 'object',
            maxCount: 5000,
            isHidden: true,
            isReadOnly: true,
            fields: {
                i9nId: {
                    type: 'keyString',
                    required: true,
                },
            },
            conditions: [
                {
                    field: 'designation',
                    values: [MenuSectionDesignation.Regular],
                },
            ],
        },
    },
    searchFields: [
        'name',
        'posName',
        'internalName',
        'posInternalName',
        'fulfillmentName',
        'posId',
        'i9nId',
        'description',
        'posDescription',
    ],
};
//# sourceMappingURL=menu_section_schema.js.map